// Controller for recording an agreement made outside Here For (COM-007–COM-009).
//
// This is deliberately a thin state machine around a typed mutation. It never
// checks capacity, revision, membership or eligibility: the shared server claim
// guard owns those decisions atomically.

import type { CommitmentRejection, commitments as contracts } from "@herefor/contracts";
import { useCallback, useRef, useState } from "react";

export type ProxyAgreementSource = contracts.ProxyAgreementSource;
export type ProxyRecordResultLike = contracts.RecordProxyCommitmentResult;

export interface ProxyRecordChoice {
  readonly responsiblePersonId: string;
  readonly agreementSource: ProxyAgreementSource;
}

export type ProxyCommitmentState =
  | { readonly status: "idle" }
  | { readonly status: "submitting" }
  | { readonly status: "accepted"; readonly responsiblePersonId: string }
  | { readonly status: "refused"; readonly rejection: CommitmentRejection };

export interface UseProxyCommitmentOptions {
  readonly record: (choice: ProxyRecordChoice) => Promise<ProxyRecordResultLike>;
  readonly onPlanChanged?: () => void;
}

export interface ProxyCommitmentController {
  readonly state: ProxyCommitmentState;
  readonly submit: (choice: ProxyRecordChoice) => Promise<void>;
  readonly reset: () => void;
}

export function useProxyCommitment({
  record,
  onPlanChanged,
}: UseProxyCommitmentOptions): ProxyCommitmentController {
  const [state, setState] = useState<ProxyCommitmentState>({ status: "idle" });
  const inFlight = useRef(false);

  const submit = useCallback(
    async (choice: ProxyRecordChoice) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setState({ status: "submitting" });

      let result: ProxyRecordResultLike;
      try {
        result = await record(choice);
      } catch {
        inFlight.current = false;
        setState({ status: "refused", rejection: { kind: "validation_failed", issues: [] } });
        return;
      }

      inFlight.current = false;
      if (!result.ok) {
        setState({ status: "refused", rejection: result.rejection });
        return;
      }

      setState({ status: "accepted", responsiblePersonId: choice.responsiblePersonId });
      onPlanChanged?.();
    },
    [record, onPlanChanged],
  );

  const reset = useCallback(() => {
    inFlight.current = false;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
